import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Exam|Details|Form|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Exam,
	portal: 'SP',
};

type LocNameStart = 'SPExamCatalogDetailsForm';
export type SPExamCatalogDetailsFormLocNames =
	| `${LocNameStart}NotificationUpdated`
	| `${LocNameStart}LabelGroup`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}ErrorNameRequired`
	| `${LocNameStart}LabelNameAbbr`
	| `${LocNameStart}LabelDisplayOrder`
	| `${LocNameStart}ErrorDisplayOrderNumber`
	| `${LocNameStart}LabelStartDate`
	| `${LocNameStart}LabelEndDate`
	| `${LocNameStart}LabelCriteriaUrl`
	| `${LocNameStart}LabelSchedulingUrl`
	| `${LocNameStart}LabelShowExag`
	| `${LocNameStart}LabelDescription`
	| `${LocNameStart}ErrorStartEndDates`
	| `${LocNameStart}ErrorInvalidUrl`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonUpdate`
	| `${LocNameStart}LabelMaximizePoints`
	| `${LocNameStart}LabelPoints`
	| `${LocNameStart}ErrorPointsNumber`;

export const spExamCatalogDetailsFormLocs: LocDefs<SPExamCatalogDetailsFormLocNames> = {
	SPExamCatalogDetailsFormNotificationUpdated: {
		defaultValue: 'Exam details updated',
		description: 'Notification message for when exam details are updated',
		displayPath: `${pathStart}Exam updated`,
		displayType: 'Notification',
		legacyName: 'Exam/Catalogs.ExamDetails.NotificationMessage.Updated',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormLabelGroup: {
		defaultValue: 'Group',
		description: 'Label for: Group',
		displayPath: `${pathStart}Group`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.Group',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormLabelName: {
		defaultValue: 'Name',
		description: 'Label for: Name',
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.Name',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormErrorNameRequired: {
		defaultValue: 'Name is required',
		description: 'Error message for when name is required',
		displayPath: `${pathStart}Name Required`,
		displayType: 'Error',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldError.Name',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormLabelNameAbbr: {
		defaultValue: 'Name Abbreviation',
		description: 'Label for: Name Abbreviation',
		displayPath: `${pathStart}Name Abbreviation`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.Abbreviation',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormLabelDisplayOrder: {
		defaultValue: 'Display Order',
		description: 'Label for: Display Order',
		displayPath: `${pathStart}Display Order`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.DisplayOrder',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormErrorDisplayOrderNumber: {
		defaultValue: 'Display order must be a number',
		description: 'Error message for when display order is not a number',
		displayPath: `${pathStart}Display Order Number`,
		displayType: 'Error',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldError.DisplayOrderNumber',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormLabelStartDate: {
		defaultValue: 'Start Date',
		description: 'Label for: Start Date',
		displayPath: `${pathStart}Start Date`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.StartDate',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormLabelEndDate: {
		defaultValue: 'End Date',
		description: 'Label for: End Date',
		displayPath: `${pathStart}End Date`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.EndDate',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormLabelCriteriaUrl: {
		defaultValue: 'Criteria URL',
		description: 'Label for: Criteria URL',
		displayPath: `${pathStart}Criteria URL`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.CriteriaURL',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormLabelSchedulingUrl: {
		defaultValue: 'Scheduling URL',
		description: 'Label for: Scheduling URL',
		displayPath: `${pathStart}Scheduling URL`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.SchedulingLink',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormLabelShowExag: {
		defaultValue: 'Show exam with altered grade',
		description: 'Label for: Show exam with altered grade',
		displayPath: `${pathStart}Show exam with altered grade`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.ShowExag',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormLabelDescription: {
		defaultValue: 'Description',
		description: 'Label for: Description',
		displayPath: `${pathStart}Description`,
		displayType: 'Label',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldLabel.Description',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormErrorStartEndDates: {
		defaultValue: 'The End Date must come after the Start Date',
		description: 'Error message for when the end date is before the start date',
		displayPath: `${pathStart}Start End Date Comparison`,
		displayType: 'Error',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldError.DateComparison',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormErrorInvalidUrl: {
		defaultValue: 'The End Date must come after the Start Date',
		description: 'Error message for when the URL is invalid',
		displayPath: `${pathStart}Invalid URL`,
		displayType: 'Error',
		legacyName: 'Exam/Catalogs.ExamDetails.FieldError.InvalidURL',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormButtonCancel: {
		defaultValue: 'Cancel',
		description: 'Cancel button',
		displayPath: `${pathStart}Cancel`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.ExamDetails.Button.Cancel',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormButtonUpdate: {
		defaultValue: 'Update',
		description: 'Update button',
		displayPath: `${pathStart}Update`,
		displayType: 'Button',
		legacyName: 'Exam/Catalogs.ExamDetails.Button.Update',
		...sharedProperties,
	},
	SPExamCatalogDetailsFormLabelMaximizePoints: {
		defaultValue: 'Maximize Points',
		description: 'Label for: Maximize Points',
		displayPath: `${pathStart}Maximize Points`,
		displayType: 'Label',
		feature: HubFeature.CredentialContinuingEducationCiscoCustomDisplayExamCatalogPoints,
		portal: 'SP',
	},
	SPExamCatalogDetailsFormLabelPoints: {
		defaultValue: 'Points',
		description: 'Label for: Points',
		displayPath: `${pathStart}Points`,
		displayType: 'Label',
		feature: HubFeature.CredentialContinuingEducationCiscoCustomDisplayExamCatalogPoints,
		portal: 'SP',
	},
	SPExamCatalogDetailsFormErrorPointsNumber: {
		defaultValue: 'Points must be a positive number',
		description: 'Error message for when points is not a number',
		displayPath: `${pathStart}Points Number`,
		displayType: 'Error',
		feature: HubFeature.CredentialContinuingEducationCiscoCustomDisplayExamCatalogPoints,
		portal: 'SP',
	},
};

export type SPExamCatalogDetailsFormLocType = LocMap<SPExamCatalogDetailsFormLocNames>;
